import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import apiClient from '../lib/api';
import { Sparkles, FileText } from 'lucide-react';
import ToSModal from './ToSModal';
import { getLatestToS, acceptToS } from '../api/terms';
import {
  validateBusinessName,
  validatePhoneNumberId,
  validateSystemPrompt,
} from '../utils/formValidation';

interface CreateBusinessFormProps {
  onSuccess: () => void;
}

const DEFAULT_SYSTEM_PROMPT = `אתה עוזר עסקי מועיל. אתה עוזר ללקוחות עם שאילתות, הזמנות ומידע כללי על השירותים שלנו.`;

export default function CreateBusinessForm({ onSuccess }: CreateBusinessFormProps) {
  const [businessName, setBusinessName] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ToS state
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [tosContent, setTosContent] = useState('');
  const [tosVersion, setTosVersion] = useState('');
  const [tosDate, setTosDate] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [tosLoading, setTosLoading] = useState(false);
  const [tosError, setTosError] = useState<string | null>(null);

  const loadToS = async () => {
    setTosLoading(true);
    setTosError(null);
    try {
      const tos = await getLatestToS();
      setTosContent(tos.content);
      setTosVersion(tos.version);
      setTosDate(tos.date || '');
    } catch (err) {
      console.error('Failed to load ToS:', err);
      setTosError('לא ניתן לטעון את תנאי השימוש. נסה שוב.');
      setTosContent('');
    } finally {
      setTosLoading(false);
    }
  };

  // Load ToS on mount
  useEffect(() => {
    loadToS();
  }, []);

  // Load ToS when modal opens if content is empty (retry / lazy load)
  useEffect(() => {
    if (tosModalOpen && !tosContent && !tosLoading && !tosError) {
      loadToS();
    }
  }, [tosModalOpen]);

  const handleToSAccept = async () => {
    setTosAccepted(true);
    setTosModalOpen(false);
  };

  const runValidation = () => {
    const nameRes = validateBusinessName(businessName);
    const phoneRes = validatePhoneNumberId(phoneNumberId);
    const promptRes = validateSystemPrompt(systemPrompt);
    const errs: Record<string, string> = {};
    if (!nameRes.valid) errs.businessName = nameRes.message;
    if (!phoneRes.valid) errs.phoneNumberId = phoneRes.message;
    if (!promptRes.valid) errs.systemPrompt = promptRes.message;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleBlur = (field: string) => {
    setTouched((p) => ({ ...p, [field]: true }));
    if (field === 'businessName') {
      const res = validateBusinessName(businessName);
      setFieldErrors((p) => ({ ...p, businessName: res.valid ? '' : res.message }));
    }
    if (field === 'phoneNumberId') {
      const res = validatePhoneNumberId(phoneNumberId);
      setFieldErrors((p) => ({ ...p, phoneNumberId: res.valid ? '' : res.message }));
    }
    if (field === 'systemPrompt') {
      const res = validateSystemPrompt(systemPrompt);
      setFieldErrors((p) => ({ ...p, systemPrompt: res.valid ? '' : res.message }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!tosAccepted) {
      setError('יש לאשר את תנאי השימוש כדי להמשיך');
      return;
    }
    if (!runValidation()) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create business (include isTosAccepted so backend persists the boolean)
      const response = await apiClient.post('/api/business', {
        name: businessName,
        phoneNumberId,
        systemPrompt,
        services: [],
        openingHours: {},
        location: '',
        isTosAccepted: tosAccepted,
      });

      const businessId = response.data.id;
      const token = localStorage.getItem('supabase.auth.token');
      
      // Accept ToS for the business
      if (token) {
        try {
          await acceptToS(businessId, tosVersion, token);
        } catch (tosErr) {
          console.error('Failed to record ToS acceptance:', tosErr);
          // Continue anyway - business is created
        }
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'יצירת פרופיל העסק נכשלה');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">
            ברוכים הבאים! בואו נגדיר את הבוט שלכם
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            אנחנו צריכים כמה פרטים כדי להפעיל את העוזר שלכם ב-WhatsApp
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Business Name */}
          <div className="mb-6">
            <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1.5">
              שם העסק *
            </label>
            <input
              id="businessName"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              onBlur={() => handleBlur('businessName')}
              required
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px] text-base ${
                touched.businessName && fieldErrors.businessName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="שם העסק"
            />
            {touched.businessName && fieldErrors.businessName && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.businessName}</p>
            )}
          </div>

          {/* WhatsApp Phone Number ID */}
          <div className="mb-6">
            <label htmlFor="phoneNumberId" className="block text-sm font-medium text-gray-700 mb-1.5">
              מזהה מספר WhatsApp *
            </label>
            <input
              id="phoneNumberId"
              type="text"
              inputMode="numeric"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value.replace(/\D/g, ''))}
              onBlur={() => handleBlur('phoneNumberId')}
              required
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm sm:text-base min-h-[44px] ${
                touched.phoneNumberId && fieldErrors.phoneNumberId ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="123456789012345"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              ניתן למצוא זאת ב-Meta Business Manager → WhatsApp → API Setup
            </p>
            {touched.phoneNumberId && fieldErrors.phoneNumberId && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.phoneNumberId}</p>
            )}
          </div>

          {/* System Prompt */}
          <div className="mb-6">
            <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700 mb-1.5">
              הנחיות מערכת ראשוניות
            </label>
            <textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              onBlur={() => handleBlur('systemPrompt')}
              rows={6}
              className={`w-full px-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm sm:text-base ${
                touched.systemPrompt && fieldErrors.systemPrompt ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="תאר איך הבוט שלך צריך להתנהג..."
            />
            <p className="mt-1.5 text-xs text-gray-500">
              תוכל להתאים אישית את זה מאוחר יותר עם מידע עסקי ספציפי יותר
            </p>
            {touched.systemPrompt && fieldErrors.systemPrompt && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.systemPrompt}</p>
            )}
          </div>

          {/* ToS Acceptance */}
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="tos-checkbox"
                checked={tosAccepted}
                onChange={(e) => {
                  if (e.target.checked) {
                    setTosModalOpen(true);
                  } else {
                    setTosAccepted(false);
                  }
                }}
                className="mt-1 ml-3 h-5 w-5 text-black focus:ring-black border-gray-300 rounded"
              />
              <label htmlFor="tos-checkbox" className="text-sm text-gray-700">
                קראתי ואני מסכים/ה ל
                <button
                  type="button"
                  onClick={() => setTosModalOpen(true)}
                  className="text-black font-medium hover:underline mr-1"
                >
                  <FileText className="inline w-4 h-4 ml-1" />
                  תנאי השימוש והתקנון
                </button>
                *
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-start">
            <button
              type="submit"
              disabled={
                loading ||
                !tosAccepted ||
                !validateBusinessName(businessName).valid ||
                !validatePhoneNumberId(phoneNumberId).valid ||
                !validateSystemPrompt(systemPrompt).valid
              }
              className="flex items-center justify-center gap-2 bg-black text-white py-2.5 px-6 rounded-md font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] w-full sm:w-auto text-base"
            >
              <Sparkles className="w-4 h-4" />
              <span>{loading ? 'יוצר...' : 'צור בוט'}</span>
            </button>
          </div>
        </form>

        {/* ToS Modal */}
        <ToSModal
          isOpen={tosModalOpen}
          onClose={() => setTosModalOpen(false)}
          onAccept={handleToSAccept}
          tosContent={tosContent}
          tosVersion={tosVersion}
          tosDate={tosDate}
          loading={tosLoading}
          error={tosError}
          onRetry={loadToS}
        />
      </div>
    </div>
  );
}
